import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface TaskTimePickerProps {
  /** Horário no formato `HH:mm` (ou string vazia quando sem horário). */
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Permite limpar o horário selecionado (botão "x"). */
  clearable?: boolean;
  /** Intervalo dos minutos exibidos na lista (padrão 5). */
  minuteStep?: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);

/** Divide `HH:mm` em hora/minuto numéricos, retornando `null` quando inválido. */
function parseTime(value: string): { hour: number; minute: number } | null {
  if (!value) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { hour: h, minute: m };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Seletor de horário compartilhado das tarefas: mantém o mesmo padrão visual do
 * `TaskDatePicker` (botão outline + popover), mas com duas listas roláveis de
 * horas e minutos. Trabalha com strings `HH:mm`, compatível com o estado que
 * antes usava `<input type="time">`.
 */
export function TaskTimePicker({
  value,
  onChange,
  id,
  disabled = false,
  placeholder = "Sem horário",
  className,
  clearable = false,
  minuteStep = 5,
}: TaskTimePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseTime(value);

  const minutes = useMemo(() => {
    const base: number[] = [];
    for (let m = 0; m < 60; m += minuteStep) base.push(m);
    // Garante que um minuto "quebrado" (vindo de due_at) apareça na lista.
    if (selected && !base.includes(selected.minute)) {
      base.push(selected.minute);
      base.sort((a, b) => a - b);
    }
    return base;
  }, [minuteStep, selected]);

  const showClear = clearable && Boolean(value) && !disabled;

  const handleHourSelect = (hour: number) => {
    const minute = selected?.minute ?? 0;
    onChange(`${pad(hour)}:${pad(minute)}`);
  };

  const handleMinuteSelect = (minute: number) => {
    const hour = selected?.hour ?? 0;
    onChange(`${pad(hour)}:${pad(minute)}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            className,
          )}
        >
          <Clock className="mr-2 h-4 w-4 shrink-0" />
          <span className="flex-1 truncate">
            {selected ? `${pad(selected.hour)}:${pad(selected.minute)}` : placeholder}
          </span>
          {showClear && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Limpar horário"
              className="ml-2 inline-flex h-4 w-4 items-center justify-center rounded-sm opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex divide-x">
          <TimeColumn
            label="Hora"
            options={HOURS}
            selectedValue={selected?.hour ?? null}
            onSelect={handleHourSelect}
            scrollActive={open}
          />
          <TimeColumn
            label="Min"
            options={minutes}
            selectedValue={selected?.minute ?? null}
            onSelect={handleMinuteSelect}
            scrollActive={open}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface TimeColumnProps {
  label: string;
  options: number[];
  selectedValue: number | null;
  onSelect: (value: number) => void;
  scrollActive?: boolean;
}

function TimeColumn({
  label,
  options,
  selectedValue,
  onSelect,
  scrollActive = false,
}: TimeColumnProps) {
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!scrollActive || selectedValue === null) return;
    const frame = requestAnimationFrame(() => {
      selectedRef.current?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollActive, selectedValue]);

  return (
    <div className="flex w-20 flex-col">
      <div className="shrink-0 border-b px-2 py-1.5 text-center text-xs font-medium text-muted-foreground">
        {label}
      </div>
      <div
        className="max-h-[160px] overflow-y-auto overscroll-contain touch-pan-y"
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-0.5 p-1">
          {options.map((option) => (
            <Button
              key={option}
              ref={selectedValue === option ? selectedRef : undefined}
              type="button"
              variant={selectedValue === option ? "default" : "ghost"}
              size="sm"
              className="h-8 justify-center font-normal tabular-nums"
              onClick={() => onSelect(option)}
            >
              {pad(option)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
